import {
  extractArticleSectionsFromBody,
  sanitizeArticle,
} from "@faire/help-center-utils/lib/articleFormatter";
import { getLatestDataUpdatedAt } from "@faire/help-center-utils/lib/getLatestDataUpdatedAt";
import { localeString } from "@faire/help-center-utils/lib/localeString";
import { articleBodyPlaceholder } from "@faire/help-center-utils/lib/random";
import { isHiddenSection } from "@faire/help-center/consts";
import { useSectionStore } from "@faire/help-center/hooks/useSectionStore";
import { useQuery as useZendeskArticles } from "@faire/web-api--source/api/zendesk/locale/articles/articleId/get-hooks";
import { trackHelpCenterArticleViewTraceError } from "@faire/web-api--source/events/helpCenter/view/article";
import home from "@faire/web-api--source/routes/support";
import { useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { useArticleContextStore } from "./ArticleContext";
import { useArticleId } from "./useArticleId";

export const useArticlesDataFetch = () => {
  const history = useHistory();
  const articleId = useArticleId();
  const [{ articleData, isLoading }, setArticleContextStore] =
    useArticleContextStore(["articleData", "isLoading"]);
  const {
    isLoading: isSectionStoreLoading,
    dataUpdatedAt: sectionStoreDataUpdatedAt,
  } = useSectionStore();
  const {
    data: zendeskArticleRawResponse,
    isLoading: isLoadingArticle,
    isError: isZendeskArticleError,
    fetchStatus: fetchStatusArticle,
    isFetching: isFetchingArticle,
    dataUpdatedAt: articlesDataUpdatedAt,
    isSuccess,
  } = useZendeskArticles(
    localeString,
    articleId ?? "",
    { include: undefined },
    {
      enabled:
        !!articleId &&
        articleData &&
        articleData.article &&
        articleId !== String(articleData.article.id),
    },
    {
      rawResponse: true,
    }
  );
  if (isZendeskArticleError) {
    trackHelpCenterArticleViewTraceError();
    history.push(home());
  }
  const { data: zendeskArticleDataInCurrentLocale, status: statusCode } =
    zendeskArticleRawResponse ?? {};
  const { data: zendeskArticleDataInEnglish } = useZendeskArticles(
    "en-us",
    articleId ?? "",
    { include: undefined },
    {
      enabled: localeString !== "en-us" && statusCode === 204,
    }
  );

  // Use the article data in the current locale if it exists, otherwise use the English version
  const zendeskArticleData =
    zendeskArticleDataInCurrentLocale ?? zendeskArticleDataInEnglish;

  const currentSectionId = zendeskArticleData?.article?.section_id;
  const articleSection = zendeskArticleData?.sections?.[0];

  useEffect(() => {
    setArticleContextStore({
      articleData: zendeskArticleData,
      isLoading:
        (isLoadingArticle && fetchStatusArticle !== "idle") ||
        isSectionStoreLoading,
    });
  }, [
    zendeskArticleData,
    isLoadingArticle,
    fetchStatusArticle,
    isSectionStoreLoading,
    setArticleContextStore,
  ]);

  const article = articleData?.article;
  const sanitizedArticleBody = useMemo((): string => {
    if (!article?.body) {
      return articleBodyPlaceholder;
    }
    return sanitizeArticle(article.body);
  }, [article?.body]);
  const articleSections = article?.body
    ? extractArticleSectionsFromBody(article.body)
    : undefined;
  const inHiddenSection = article && isHiddenSection(article.section_id);
  const dataUpdatedAt = getLatestDataUpdatedAt([
    sectionStoreDataUpdatedAt,
    articlesDataUpdatedAt,
  ]);
  return {
    article,
    isLoading: isLoading || isFetchingArticle || isSectionStoreLoading,
    sanitizedArticleBody,
    articleSections,
    inHiddenSection,
    articleSection,
    currentSectionId,
    dataUpdatedAt,
    isSuccess,
  };
};
